import React, { memo, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { AccountInfo } from '../../../components/Layout/AccountInfo'
import CreatePromo from '../../../containers/CreatePromo'
import CreatePushNotification from '../../../containers/CreatePushNotification'
import { constants } from '../../../constants'
import { LanguageContext } from '../../../context/LanguageContext'
import LanguageSelector from '../../common/LanguageSelector'
import { Menu, Icon } from '../../../components/common'
import sevenNowLogo from '../../../assets/images/7now-logo-2x.png'
import { util } from '../../../util'

const Header = ({ user_details, logout, toggleHandler }) => {
    const { rtl, strings } = useContext(LanguageContext)
    const adp_configurations = useSelector(({ common }) => common.adp_configurations)

    const user_permissions = useSelector(({ auth }) => auth.user_permissions)

    const navigate = useNavigate()

    const renderCreateNew = () => (
        <>
            <Icon
                size={'sm'}
                className={'add-button'}
                display={'inline-block'}
                renderIcon={util.AddIcon}
            />
            <p className={'paragraph bold mt--1'}>{strings.HEADER_CREATE_NEW_BUTTON}</p>
        </>
    )

    const renderProductPage = () => {
        return navigate(constants.routePaths.add_new_products.path)
    }

    const renderMenuItem = (menuItem, permission, config_feature_key) => {
        if (!adp_configurations || !adp_configurations.feature_flag) {
            return null
        }
        if (config_feature_key) {
            const { is_active } = adp_configurations.feature_flag.find(
                ({ key }) => key === config_feature_key
            )
            if (!is_active) {
                return null
            }
        }
        if (user_permissions.includes(permission)) {
            return menuItem()
        }
    }

    const renderStoreImportPage = () => {
        return navigate(constants.routePaths.add_new_store_import.path)
    }
    const renderAddStoreImport = () => {
        return (
            <Menu.MenuItem onClick={renderStoreImportPage}>
                <Icon
                    size={'md'}
                    pointer
                    className={'createNewDropdown mr-1'}
                    renderIcon={util.StoreIcon2}
                />
                <p className={'mt--1 hover'}>{strings.CREATE_NEW_STORE_IMPORT}</p>
            </Menu.MenuItem>
        )
    }

    const renderProductImportPage = () => {
        return navigate(constants.routePaths.add_new_product_import.path)
    }
    const renderAddProductImport = () => {
        return (
            <Menu.MenuItem onClick={renderProductImportPage}>
                <Icon
                    size={'md'}
                    pointer
                    className={'createNewDropdown mr-1'}
                    renderIcon={util.ProductImportIcon}
                />
                <p className={'mt--1 hover'}>{strings.CREATE_NEW_PRODUCT_IMPORT}</p>
            </Menu.MenuItem>
        )
    }

    const renderInventoryImportPage = () => {
        return navigate(constants.routePaths.add_new_inventory_import.path)
    }
    const renderInventoryImport = () => {
        return (
            <Menu.MenuItem onClick={renderInventoryImportPage}>
                <Icon
                    size={'md'}
                    pointer
                    className={'createNewDropdown mr-1'}
                    renderIcon={util.InventoryImportIcon}
                />
                <p className={'mt--1 hover'}>{strings.CREATE_NEW_INVENTORY_IMPORT}</p>
            </Menu.MenuItem>
        )
    }

    const renderCreatePromo = () => {
        return (
            <Menu.MenuItem onClick={props => <CreatePromo {...props} />}>
                <Icon
                    size={'md'}
                    pointer
                    className={'createNewDropdown mr-1'}
                    renderIcon={util.PromotionsIcon}
                />
                <p className={'mt--1 hover'}>{strings.CREATE_NEW_PROMOTION}</p>
            </Menu.MenuItem>
        )
    }

    const renderCreatePushNotification = () => {
        return (
            <Menu.MenuItem onClick={props => <CreatePushNotification {...props} />}>
                <Icon
                    size={'md'}
                    pointer
                    className={'createNewDropdown mr-1'}
                    renderIcon={util.PushNotificationsIcon}
                />
                <p className={'mt--1 hover'}>{strings.CREATE_NEW_PUSH_NOTIFICATION}</p>
            </Menu.MenuItem>
        )
    }

    const renderAddProduct = () => {
        return (
            <Menu.MenuItem onClick={() => renderProductPage()}>
                <Icon
                    size={'md'}
                    pointer
                    className={'createNewDropdown mr-1'}
                    renderIcon={util.ProductIcon}
                />
                <p className={'mt--1 hover'}>{strings.CREATE_NEW_PRODUCT}</p>
            </Menu.MenuItem>
        )
    }

    return (
        <header className={'header'}>
            <div className={'header__top-bar'} />
            <div className={'toolbar'}>
                <Icon
                    className={'menu-icon'}
                    pointer={true}
                    renderIcon={util.MenuIcon}
                    onClick={toggleHandler}
                />
                <div className={'toolbar__heading heading-secondary'}>
                    <img className={'sevenNowLogo'} src={sevenNowLogo} alt={'7-now'} />
                    <div className={'vertical-line'} />
                    <p className={'heading-primary mr-2 ml-2'}>{strings.HEADER_TITLE}</p>
                </div>
                <div className={'toolbar--actions'}>
                    <LanguageSelector />
                    {user_permissions.some(user_permission =>
                        user_permission.endsWith('-WRITE')
                    ) && (
                        <Menu
                            placement={rtl ? 'right' : 'middle'}
                            tipPlacement={'middle'}
                            className={'create-menu-list'}
                            hover={true}
                            margin={'mt-3'}
                            renderProps={renderCreateNew}
                        >
                            {renderMenuItem(
                                renderCreatePromo,
                                constants.permissions['APP-7NOWMANAGER-PROMOTION-WRITE'],
                                constants.configFeatureFlags.PROMOTIONS
                            )}

                            {renderMenuItem(
                                renderCreatePushNotification,
                                constants.permissions['APP-7NOWMANAGER-NOTIFICATION-WRITE'],
                                constants.configFeatureFlags.PUSH_NOTIFICATIONS
                            )}

                            {renderMenuItem(
                                renderAddStoreImport,
                                constants.permissions['APP-7NOWMANAGER-STOREIMPORT-WRITE'],
                                constants.configFeatureFlags.MANAGEMENT_DATA
                            )}
                            {renderMenuItem(
                                renderAddProductImport,
                                constants.permissions['APP-7NOWMANAGER-PRODUCTIMPORT-WRITE'],
                                constants.configFeatureFlags.MANAGEMENT_DATA
                            )}
                            {renderMenuItem(
                                renderInventoryImport,
                                constants.permissions['APP-7NOWMANAGER-INVENTORYIMPORT-WRITE'],
                                constants.configFeatureFlags.MANAGEMENT_DATA
                            )}

                            {renderMenuItem(
                                renderAddProduct,
                                constants.permissions['APP-7NOWMANAGER-PRODUCT-WRITE'],
                                constants.configFeatureFlags.ASSORTMENT
                            )}
                        </Menu>
                    )}
                    <AccountInfo userDetails={user_details} logout={logout} />
                </div>
            </div>
        </header>
    )
}

export default memo(Header)
